import * as React from 'react';
import { Download, ShieldAlert, TriangleAlert } from 'lucide-react';
import { api, isApiError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { SectionCard } from './SectionCard';
import { candidatePath, downloadBlob } from './api';
import type { CandidateDetail } from './types';

export interface GdprPanelProps {
  candidate: Pick<CandidateDetail, 'id' | 'candidateId' | 'consentDate' | 'erasedAt' | 'computed'>;
  retentionMonths: number;
  canWrite: boolean;
  isAdmin: boolean;
  /** `PATCH /candidates/:id` */
  onPatch: (patch: { consentDate: string | null }) => Promise<unknown>;
  /** Called after a successful erase (refetch the detail). */
  onErased?: () => void;
}

/**
 * GDPR (spec §6.3): consent date and expiry (amber within 30 days), JSON export and the admin-only
 * erase flow (confirmation dialog with a reason → `POST /candidates/:id/erase`).
 */
export function GdprPanel({
  candidate,
  retentionMonths,
  canWrite,
  isAdmin,
  onPatch,
  onErased,
}: GdprPanelProps) {
  const erased = Boolean(candidate.erasedAt);
  const [eraseOpen, setEraseOpen] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [savingConsent, setSavingConsent] = React.useState(false);
  const consentId = React.useId();
  const soon = candidate.computed.consentExpiresSoon;

  const exportData = async () => {
    setExporting(true);
    try {
      const data = await api.get<unknown>(`${candidatePath(candidate.id)}/export`);
      downloadBlob(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
        `${candidate.candidateId}_export.json`,
      );
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const setConsent = async (iso: string | null) => {
    if (iso === candidate.consentDate) return;
    setSavingConsent(true);
    try {
      await onPatch({ consentDate: iso });
      toast.success('Consent date saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the consent date');
    } finally {
      setSavingConsent(false);
    }
  };

  return (
    <SectionCard
      title="GDPR"
      description={`Consent is kept for ${retentionMonths} months; the expiry is computed from the consent date.`}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => void exportData()} loading={exporting}>
            <Download aria-hidden="true" /> Export candidate data
          </Button>
          {isAdmin && !erased ? (
            <Button variant="destructive" size="sm" onClick={() => setEraseOpen(true)}>
              <ShieldAlert aria-hidden="true" /> Erase candidate
            </Button>
          ) : null}
        </>
      }
    >
      {erased ? (
        <div
          role="status"
          className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">Personal data erased on {formatDateTime(candidate.erasedAt)}.</p>
            <p>
              Only the anonymised stub ({candidate.candidateId}) remains so the submission history stays
              consistent.
            </p>
          </div>
        </div>
      ) : null}
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Label
              htmlFor={consentId}
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Consent date
            </Label>
          </dt>
          <dd className="mt-1 text-sm">
            {canWrite && !erased ? (
              <DateInput
                id={consentId}
                value={candidate.consentDate}
                onChange={(iso) => void setConsent(iso)}
                disabled={savingConsent}
                className="h-8"
              />
            ) : candidate.consentDate ? (
              <time dateTime={candidate.consentDate}>{formatDate(candidate.consentDate)}</time>
            ) : (
              <span className="text-muted-foreground">–</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Consent expiry
          </dt>
          <dd className={cn('mt-1 flex items-center gap-1.5 text-sm', soon && 'font-medium text-warning')}>
            {candidate.computed.consentExpiry ? (
              <>
                {soon ? <TriangleAlert className="h-4 w-4" aria-label="Consent expires soon" /> : null}
                <time dateTime={candidate.computed.consentExpiry}>
                  {formatDate(candidate.computed.consentExpiry)}
                </time>
                {soon ? <span className="text-xs font-normal">(within 30 days)</span> : null}
              </>
            ) : (
              <span className="text-muted-foreground">–</span>
            )}
          </dd>
        </div>
      </dl>

      <EraseDialog
        open={eraseOpen}
        onOpenChange={setEraseOpen}
        candidateId={candidate.candidateId}
        onConfirm={async (reason) => {
          await api.post(`${candidatePath(candidate.id)}/erase`, { confirm: true, reason });
        }}
        onDone={() => {
          setEraseOpen(false);
          toast.success(`${candidate.candidateId} erased`);
          onErased?.();
        }}
      />
    </SectionCard>
  );
}

function EraseDialog({
  open,
  onOpenChange,
  candidateId,
  onConfirm,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: string;
  onConfirm: (reason: string) => Promise<void>;
  onDone: () => void;
}) {
  const [reason, setReason] = React.useState('');
  const [confirmed, setConfirmed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const reasonId = React.useId();
  const confirmId = React.useId();

  React.useEffect(() => {
    if (open) {
      setReason('');
      setConfirmed(false);
      setError(null);
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 3 || !confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onDone();
    } catch (err) {
      setError(isApiError(err) ? err.message : err instanceof Error ? err.message : 'Erase failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Erase {candidateId}?</DialogTitle>
            <DialogDescription>
              Removes the name, contact details, CV files, generated profiles and notes. The ID and the
              submission history stay as an anonymised stub. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={reasonId}>Reason</Label>
            <Textarea
              id={reasonId}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Candidate requested deletion by e-mail on 12.09.2026"
              autoFocus
              required
              minLength={3}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id={confirmId} checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} />
            <Label htmlFor={confirmId} className="font-normal">
              I understand the personal data will be deleted permanently.
            </Label>
          </div>
          {error ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              loading={busy}
              disabled={reason.trim().length < 3 || !confirmed}
            >
              Erase permanently
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
